import React, { useContext, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { generateClient } from '@aws-amplify/api';
import RootContext from '@/RootContext';
import SubjectEntry from '@/SubjectEntry';
import SubjectsList from '@/SubjectsList';
import SubjectEditorTabs from '@/SubjectEntry/SubjectEditorTabs';

const client = generateClient();

const SpinnerLine = () => (
    <span className="spinner-border spinner-border-sm" style={{ width: '1.5rem', height: '1.5rem', borderWidth: '2px', opacity:0.5, zIndex:1 }} />
);

const Name = ({ id, type }) => {
  const [name, setName] = useState(<SpinnerLine />);
  const isConstructed = useRef(false);
  if (!isConstructed.current) {
    isConstructed.current = true;
    (async () => {
      try {
        switch (type) {
          case 'Subject': return setName((await client.graphql({ query: 'query GetSubjects($id: [Int]) { getSubjects(id: $id) { subjects { full_name_last } } }', variables: { id } }))?.data.getSubjects.subjects?.[0].full_name_last);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }
  return name;
};

const new_id = ({ id, type }) => {
  return id === 'new' ? `New ${type}` : <Name id={id} type={type} />;
};

const stripSearch = search => {
	const queryParams = new URLSearchParams(search);
	queryParams.delete('tab');
	return queryParams.toString();
};

const delete_search = ({ key, search }) => {
  const _key = Array.isArray(key) ? key : [key];
  const queryParams = new URLSearchParams(search);
  _key.forEach(e => queryParams.delete(e));
	return queryParams.toString();
};

const SubjectListDetail = props => {
  const { id } = useParams();
  const navigate = useNavigate();
  const rootContext = useContext(RootContext);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const enrollment_status = queryParams.get('enrollment_status') || undefined;
  const deal_status = queryParams.get('deal_status') || undefined;

  let links = [];

  const close = () => navigate({ pathname: '/app/pipeline/subjects', search: delete_search({ key: 'tab', search: location.search }) });

  const url0 = new URLSearchParams(location.search);
  const recruitment_name = url0.get('recruitment_name');
  const site_name = url0.get('site_name');
  const study_name = url0.get('study_name');
  const root_name = recruitment_name || site_name || study_name;
  const url1 = new URLSearchParams(location.search);
  url1.delete('enrollment_status');
  url1.delete('deal_status');

  if (id || root_name || enrollment_status || deal_status) {
    links.push({ label: 'Subjects', url: { pathname: '/app/pipeline/subjects', search: delete_search({ key: 'tab', search: location.search }) } });
    if (root_name) links.push({ label: root_name, ...(id || enrollment_status || deal_status ? { url: { pathname: '/app/pipeline/subjects', search: url1.toString() } } : undefined) });
  }

  if (enrollment_status) {
    const parts = enrollment_status.split('::');
    parts.forEach((part, i) => {
      const url = new URLSearchParams(location.search);
      if (i === 0) {
        // Replace root with rollup root based on priority
        const enrollment_statuses = rootContext.get('enrollment_statuses');
        const found = (enrollment_statuses || []).find(f => f.id.split('::')[0] === parts[0]);
        let found_root;
        if (found) {
          found_root = (enrollment_statuses || []).find(f => f.id.indexOf('::') === -1 && f.priority === found.priority);
        }
        part = found_root ? found_root.id : part;
        url.set('enrollment_status', part);
      }
      links.push({ label: part, ...(id || i === 0 && parts.length > 1 ? { url: { pathname: '/app/pipeline/subjects', search: url.toString() } } : undefined) });
    });
  }

  if (deal_status) {
    const parts = deal_status.split('::');
    parts.forEach((part, i) => {
      const url = new URLSearchParams(location.search);
      if (i === 0) {
        // Replace root with rollup root based on priority
        const deal_statuses = rootContext.get('deal_statuses');
        const found = (deal_statuses || []).find(f => f.id.split('::')[0] === parts[0]);
        let found_root;
        if (found) {
          found_root = (deal_statuses || []).find(f => f.id.indexOf('::') === -1 && f.priority === found.priority);
        }
        part = found_root ? found_root.id : part;
        url.set('deal_status', part);
      }
      links.push({ label: part, ...(id || i === 0 && parts.length > 1 ? { url: { pathname: '/app/pipeline/subjects', search: url.toString() } } : undefined) });
    });
  }

  if (id) {
    links.push({ label: new_id({ id, type: 'Subject' }) });
  }

  // For now, just enable the new subject detail for certain clients
  const allowedHosts = new Set([
    'publisher.com',
    'dev.openpath.ai',
    'wcr.openpath.ai',
    'preview.openpath.ai'
  ]);
  const enableNewSubjectView = allowedHosts.has(window.location.hostname);

  return (
    <>
      {id && (
        <SubjectEditorTabs 
          {...props} 
          enableNewSubjectView={enableNewSubjectView}
          isHeader={false} 
          links={links} 
          onCancel={close} 
          onSave={close} 
        />
      )}
      {!id && <SubjectsList links={links} />}
    </>
  );
};

const EnrollmentPipeline = () => {
  const location = useLocation();

  return (
    <div className="container-fluid">
      <Routes>
        <Route path="subjects/:id?" element={<SubjectListDetail />} />
        <Route path="*" element={<Navigate to={{ pathname: 'subjects', search: location.search }} replace />} />
      </Routes>
    </div>
  );
};

export default EnrollmentPipeline;
